import { usePersonnel } from 'features/Responder/features/Personnel';
import {
  isRunOpen,
  resolveRunScheduleId,
  resolveRunStart,
} from 'features/Responder/features/Schedules/utils';
import { useTours } from 'features/Responder/features/Tours';
import {
  computeRouteMetrics,
  resolveRoutePoints,
} from 'features/Responder/features/Tours';
import { useZones } from 'features/Responder/features/Zones';
import { useResponderMap } from 'features/Responder/features/ResponderMap/contexts/ResponderMapContext';
import React, { useEffect, useMemo, useRef, useState } from 'react';
import { Badge, Button, Card, Col, Row, Spinner, Stack } from 'react-bootstrap';
import { FaCalendarAlt, FaClock, FaMapMarkerAlt, FaRoute, FaUsers } from 'react-icons/fa';
import { RRule } from 'rrule';
import apiClient from 'services/_infra/apiClient';
import { fixedRruleFromString } from 'utils/recurrenceUtils';
import { useSchedules } from 'features/Responder/features/Schedules/contexts/SchedulesContext';
import { RunHistory } from 'features/Responder/features/Schedules/features/Runs/features/RunHistory';
import { useGoogleEta } from './hooks';
import './schedule-telemetry.css';
import {
  buildTelemetryData,
  formatDistance,
  formatDuration,
  normalizeLatLng,
  parseTimestamp,
  pickStopLabel,
  resolveScheduleTour,
  resolveTourStops,
  resolveTourTravelMode,
  toSeconds,
} from './utils';

const StatTile = ({ icon: Icon, label, value }) => (
  <div className="border rounded p-2 h-100 d-flex gap-2 align-items-center">
    {Icon ? <Icon className="text-primary flex-shrink-0" /> : null}
    <div>
      <div className="text-muted text-uppercase small">{label}</div>
      <div className="fw-semibold">{value ?? 'Not available'}</div>
    </div>
  </div>
);

const TabButton = ({ active, disabled, onClick, children }) => (
  <Button
    size="sm"
    variant={active ? 'primary' : 'outline-light'}
    disabled={disabled}
    onClick={onClick}
    className={`px-3 schedule-telemetry-tab-btn ${
      active ? 'schedule-telemetry-tab-btn--active' : ''
    }`}
  >
    {children}
  </Button>
);

const normalizeTelemetryTab = (value, { allowHistory = true } = {}) => {
  if (!value) return null;
  const normalized = `${value}`
    .trim()
    .toLowerCase()
    .replace(/[_\s]+/g, '-');
  if (normalized === 'live') return 'live';
  if (normalized === 'overview') return 'overview';
  if (allowHistory && ['history', 'run-history', 'runhistory'].includes(normalized)) {
    return 'history';
  }
  return null;
};

const formatDateTime = (ts) => {
  if (!ts) return '';
  const d = new Date(ts);
  if (isNaN(d.getTime())) return ts;
  return d.toLocaleString();
};
const getEventTimestamp = (event) =>
  event?.timestamp ??
  event?.createdAt ??
  event?.created_at ??
  event?.occurredAt ??
  event?.time ??
  null;

const normalizeEventLabel = (value) => {
  if (!value || typeof value !== 'string') return '';
  const cleaned = value.replace(/[_-]+/g, ' ').trim();
  if (!cleaned) return '';
  return cleaned
    .toLowerCase()
    .split(/\s+/)
    .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
    .join(' ');
};

const getEventLabel = (event) =>
  normalizeEventLabel(event?.event || event?.type || event?.status || event?.message || 'Event');

const daysOfWeek = ['Sunday', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday'];

const dayIndexLookup = new Map([
  ['sun', 0],
  ['sunday', 0],
  ['mon', 1],
  ['monday', 1],
  ['tue', 2],
  ['tues', 2],
  ['tuesday', 2],
  ['wed', 3],
  ['weds', 3],
  ['wednesday', 3],
  ['thu', 4],
  ['thur', 4],
  ['thurs', 4],
  ['thursday', 4],
  ['fri', 5],
  ['friday', 5],
  ['sat', 6],
  ['saturday', 6],
]);

const normalizeDayIndex = (value) => {
  if (value == null) return null;
  if (typeof value === 'number' && Number.isInteger(value)) {
    return value >= 0 && value <= 6 ? value : null;
  }
  const text = `${value}`.trim().toLowerCase();
  if (!text) return null;
  if (/^\d+$/.test(text)) {
    const num = Number(text);
    return num >= 0 && num <= 6 ? num : null;
  }
  if (dayIndexLookup.has(text)) return dayIndexLookup.get(text);
  const abbr = text.slice(0, 3);
  return dayIndexLookup.has(abbr) ? dayIndexLookup.get(abbr) : null;
};

const getTimeMinutes = (ts) => {
  if (!ts) return null;
  if (ts instanceof Date) {
    return ts.getHours() * 60 + ts.getMinutes();
  }
  const raw = typeof ts === 'string' && ts.includes('T') ? ts.split('T')[1] : ts;
  const time = typeof raw === 'string' ? raw.slice(0, 5) : null;
  if (!time) return null;
  const [h, m] = time.split(':').map(Number);
  if (!Number.isFinite(h) || !Number.isFinite(m)) return null;
  return h * 60 + m;
};

const getRecurrenceStatus = (schedule, now) => {
  const recurrence = schedule?.recurrence;
  if (!recurrence || typeof recurrence !== 'string' || !recurrence.trim()) return null;
  let rule;
  try {
    rule = fixedRruleFromString(recurrence);
  } catch (err) {
    console.warn('Invalid schedule recurrence', err);
    return null;
  }

  let durationMs = Number(schedule?.duration);
  if (!Number.isFinite(durationMs) || durationMs <= 0) {
    const start = schedule?.start_time ? new Date(schedule.start_time) : null;
    const end = schedule?.end_time ? new Date(schedule.end_time) : null;
    if (start && end && !isNaN(start.getTime()) && !isNaN(end.getTime())) {
      durationMs = end.getTime() - start.getTime();
    }
  }

  const lastStart = rule.before(now, true);
  if (lastStart && Number.isFinite(durationMs) && durationMs > 0) {
    const lastEnd = new Date(lastStart.getTime() + durationMs);
    if (now >= lastStart && now <= lastEnd) {
      return { isActive: true, isFuture: false };
    }
  }

  const nextStart = rule.after(now, false);
  if (nextStart) return { isActive: false, isFuture: true };
  return { isActive: false, isFuture: false };
};

const getScheduleStatus = (schedule) => {
  if (!schedule) return { isActive: false, isFuture: false };
  const now = new Date();
  const dayLabel = daysOfWeek[now.getDay()];
  const hasDays = Array.isArray(schedule.days_of_week) && schedule.days_of_week.length > 0;

  const recurrenceStatus = getRecurrenceStatus(schedule, now);
  if (recurrenceStatus) return recurrenceStatus;

  if (hasDays) {
    const startMin = getTimeMinutes(schedule.start_time);
    const endMin = getTimeMinutes(schedule.end_time);
    const nowMin = now.getHours() * 60 + now.getMinutes();
    const daySet = new Set(
      schedule.days_of_week.map((day) => normalizeDayIndex(day)).filter((day) => day != null),
    );
    const isToday = daySet.size
      ? daySet.has(now.getDay())
      : schedule.days_of_week.includes(dayLabel);
    const isOvernight = Number.isFinite(startMin) && Number.isFinite(endMin) && endMin < startMin;
    const isInWindow = (() => {
      if (!Number.isFinite(startMin) || !Number.isFinite(endMin)) return isToday;
      if (isOvernight) return nowMin >= startMin || nowMin <= endMin;
      return nowMin >= startMin && nowMin <= endMin;
    })();
    const isFuture = !isToday || (Number.isFinite(startMin) && nowMin < startMin);
    return { isActive: isToday && isInWindow, isFuture };
  }

  const start = new Date(schedule.start_time);
  const end = new Date(schedule.end_time);
  if (isNaN(start.getTime()) || isNaN(end.getTime())) {
    return { isActive: false, isFuture: false };
  }
  return { isActive: now >= start && now <= end, isFuture: now < start };
};

const summarizePersonnel = (ids = [], lookup = new Map()) => {
  if (!Array.isArray(ids) || !ids.length) return [];
  return ids.map((id) => lookup.get(id) || `#${id}`);
};

export default function ScheduleTelemetry({
  scrollHeight = null,
  initialTab = 'live',
  showHistoryTab = true,
  historyScheduleIds = null,
  enableRunSelection = true,
}) {
  const { activeEditSchedule, runs } = useSchedules();
  const { tours, addTour } = useTours();
  const { zones } = useZones();
  const { selectedTourIds, selectedZoneIds } = useResponderMap();
  const { personnel, geolocationHistory, allEvents } = usePersonnel();
  const [fetchedTour, setFetchedTour] = useState(null);
  const tourFetchRef = useRef({ inflightId: null, lastFetchedId: null });

  const draftSelectionSchedule = useMemo(() => {
    if (activeEditSchedule) return null;
    const draftTourId =
      Array.isArray(selectedTourIds) && selectedTourIds.length
        ? selectedTourIds[0]
        : null;
    if (draftTourId != null) {
      const draftTour = Array.isArray(tours)
        ? tours.find((item) => `${item?.id}` === `${draftTourId}`) || null
        : null;
      return {
        id: null,
        name: draftTour?.name ? `${draftTour.name} Schedule` : 'Draft Schedule',
        entity: { type: 'Tour', entity_id: draftTourId },
        personnel_ids: [],
        notes: '',
      };
    }
    const draftZoneId =
      Array.isArray(selectedZoneIds) && selectedZoneIds.length
        ? selectedZoneIds[0]
        : null;
    if (draftZoneId != null) {
      const draftZone = Array.isArray(zones)
        ? zones.find((item) => `${item?.id}` === `${draftZoneId}`) || null
        : null;
      return {
        id: null,
        name: draftZone?.name ? `${draftZone.name} Schedule` : 'Draft Schedule',
        entity: { type: 'Zone', entity_id: draftZoneId },
        personnel_ids: [],
        notes: '',
      };
    }
    return null;
  }, [activeEditSchedule, selectedTourIds, selectedZoneIds, tours, zones]);

  const schedule = activeEditSchedule || draftSelectionSchedule || null;

  const tour = useMemo(
    () => resolveScheduleTour(schedule, tours, fetchedTour),
    [schedule, tours, fetchedTour],
  );

  const tourStops = useMemo(() => resolveTourStops(tour), [tour]);

  useEffect(() => {
    const targetId =
      schedule?.entity?.type === 'Tour'
        ? (schedule.entity?.entity_id ?? schedule.entity?.id ?? schedule.entityId ?? null)
        : null;
    if (!targetId) return;
    if (tour && `${tour.id}` === `${targetId}`) {
      if (fetchedTour && `${fetchedTour.id}` !== `${targetId}`) {
        setFetchedTour(null);
      }
      return;
    }

    const key = `${targetId}`;
    if (tourFetchRef.current.inflightId === key || tourFetchRef.current.lastFetchedId === key) {
      return;
    }

    tourFetchRef.current.inflightId = key;
    apiClient
      .get(`/responder/tours/${encodeURIComponent(key)}`)
      .then((res) => {
        const data = res?.data;
        const resolved =
          data?.tour ||
          data?.trip ||
          data?.result ||
          data?.data ||
          (Array.isArray(data?.tours)
            ? data.tours.find((item) => `${item?.id}` === key) || null
            : null) ||
          (data && typeof data === 'object' ? data : null);
        if (resolved) {
          setFetchedTour(resolved);
          addTour?.(resolved);
        }
      })
      .catch((err) => {
        console.error('[ScheduleTelemetry] Tour fetch failed', err);
      })
      .finally(() => {
        tourFetchRef.current.inflightId = null;
        tourFetchRef.current.lastFetchedId = key;
      });
  }, [addTour, fetchedTour, schedule, tour]);

  const zone = useMemo(() => {
    if (!schedule || schedule.entity.type !== 'Zone') return null;
    return zones.find((z) => `${z.id}` === `${schedule.entity.entity_id}`) || null;
  }, [schedule, zones]);

  const personnelLookup = useMemo(() => {
    const map = new Map();
    const list = Array.isArray(personnel) ? personnel : Object.values(personnel || {});
    list.forEach((p) => {
      const id = p.userId ?? p.id;
      if (id == null) return;
      map.set(id, p.name || p.fullName || p.username || `Personnel ${id}`);
    });
    return map;
  }, [personnel]);

  const assignedPersonnel = useMemo(
    () => summarizePersonnel(schedule?.personnel_ids || [], personnelLookup),
    [schedule?.personnel_ids, personnelLookup],
  );

  const recentPersonnelLocations = useMemo(() => {
    const ids = Array.isArray(schedule?.personnel_ids) ? schedule.personnel_ids : [];
    if (!ids.length) return [];

    const candidates = [];
    const pushCandidate = (userId, entry) => {
      const coords = normalizeLatLng(entry);
      if (!coords) return;
      const timestampMs = parseTimestamp(
        entry?.timestamp ??
          entry?.time ??
          entry?.createdAt ??
          entry?.created_at ??
          entry?.updatedAt ??
          entry?.updated_at,
      );
      candidates.push({
        userId,
        lat: coords.lat,
        lng: coords.lng,
        timestampMs,
        rawTimestamp:
          entry?.timestamp ??
          entry?.time ??
          entry?.createdAt ??
          entry?.created_at ??
          entry?.updatedAt ??
          entry?.updated_at ??
          null,
      });
    };

    ids.forEach((id) => {
      const key = `${id}`;
      const person = personnel?.[id] ?? personnel?.[key];
      if (person && person.clockedIn === false) return;
      if (Array.isArray(person?.locations)) {
        person.locations.forEach((loc) => pushCandidate(id, loc));
      }
      const history = geolocationHistory?.[id] ?? geolocationHistory?.[key];
      if (Array.isArray(history)) {
        history.forEach((loc) => pushCandidate(id, loc));
      }
    });

    if (!candidates.length) return [];
    return candidates.slice().sort((a, b) => (b.timestampMs ?? 0) - (a.timestampMs ?? 0));
  }, [geolocationHistory, personnel, schedule?.personnel_ids]);

  const latestPersonnelLocation = useMemo(
    () => (recentPersonnelLocations.length ? recentPersonnelLocations[0] : null),
    [recentPersonnelLocations],
  );

  const eventHistory = useMemo(() => {
    if (!schedule) return [];
    const targetType = schedule.entity.type;
    if (targetType !== 'Tour' && targetType !== 'Zone') return [];
    const targetId = schedule.entity.entity_id;
    if (!Array.isArray(allEvents) || targetId == null) return [];
    const matches = allEvents.filter((event) => {
      const entityId =
        event?.entityId ??
        event?.entity_id ??
        event?.tourId ??
        event?.tourId ??
        event?.tour_id ??
        event?.zoneId ??
        event?.zone_id ??
        null;
      const zoneId = event?.zoneId ?? event?.zone_id ?? null;
      const tourId = event?.tourId ?? event?.tour_id ?? event?.tourId ?? null;
      const eventEntityType = (event?.entityType ?? event?.entity_type ?? event?.entity?.type ?? '')
        .toString()
        .toLowerCase();
      const hasEventType = eventEntityType.length > 0;
      if (targetType === 'Zone') {
        if (zoneId != null && `${zoneId}` === `${targetId}`) return true;
        if (entityId != null && `${entityId}` === `${targetId}`) {
          return !hasEventType || eventEntityType === 'zone';
        }
        return false;
      }
      if (tourId != null && `${tourId}` === `${targetId}`) return true;
      if (entityId != null && `${entityId}` === `${targetId}`) {
        return !hasEventType || eventEntityType === 'tour';
      }
      return false;
    });
    return matches
      .map((event, index) => ({
        key: event?.id ?? `${getEventTimestamp(event) ?? 'event'}-${index}`,
        label: getEventLabel(event),
        timestamp: getEventTimestamp(event),
        details: event?.details || event?.notes || event?.description || '',
      }))
      .sort((a, b) => {
        const aTime = a.timestamp ? new Date(a.timestamp).getTime() : 0;
        const bTime = b.timestamp ? new Date(b.timestamp).getTime() : 0;
        return bTime - aTime;
      });
  }, [allEvents, schedule]);

  const scheduleStatus = useMemo(() => getScheduleStatus(schedule), [schedule]);

  const activeRun = useMemo(() => {
    if (!schedule?.id || !Array.isArray(runs)) return null;
    const matches = runs.filter((run) => `${resolveRunScheduleId(run)}` === `${schedule.id}`);
    if (!matches.length) return null;
    return matches
      .slice()
      .sort(
        (a, b) =>
          (parseTimestamp(resolveRunStart(b)) ?? 0) - (parseTimestamp(resolveRunStart(a)) ?? 0),
      )[0];
  }, [runs, schedule?.id]);

  const isRunActive = useMemo(() => isRunOpen(activeRun), [activeRun]);

  const showLiveTelemetry = useMemo(() => {
    if (schedule?.entity?.type !== 'Tour') return false;
    if (isRunActive) return true;
    // If the latest run exists and is ended/stopped, reset live telemetry.
    if (activeRun) return false;
    // Preserve previous behavior when there has not been a run yet.
    return scheduleStatus.isActive;
  }, [activeRun, isRunActive, schedule?.entity?.type, scheduleStatus.isActive]);

  const isActive = useMemo(() => {
    if (isRunActive) return true;
    if (activeRun) return false;
    return scheduleStatus.isActive;
  }, [activeRun, isRunActive, scheduleStatus.isActive]);

  const isFuture = isRunActive ? false : scheduleStatus.isFuture;

  const scheduleTargetLabel = tour
    ? tour.name || `Tour #${tour.id}`
    : zone
      ? zone.name || `Zone #${zone.id}`
      : schedule
        ? `Entity #${schedule.entity.entity_id}`
        : 'None';

  const routePoints = useMemo(() => {
    if (!tour) return [];
    return resolveRoutePoints(null, tour);
  }, [tour]);

  const routeMetrics = useMemo(() => {
    if (!routePoints.length) return { cum: [], total: 0 };
    return computeRouteMetrics(routePoints);
  }, [routePoints]);

  const legDistanceMeters = useMemo(() => {
    if (!tour?.legs?.length) return 0;
    return tour.legs.reduce((sum, leg) => {
      const dist = Number.isFinite(leg?.distanceMeters) ? leg.distanceMeters : 0;
      return sum + dist;
    }, 0);
  }, [tour]);

  const legDurationSeconds = useMemo(() => {
    if (!tour?.legs?.length) return 0;
    return tour.legs.reduce((sum, leg) => {
      const secs = toSeconds(leg?.duration);
      return Number.isFinite(secs) ? sum + secs : sum;
    }, 0);
  }, [tour]);

  const polylineDistanceMeters = useMemo(() => {
    if (!routePoints.length) return null;
    return routeMetrics.total || null;
  }, [routeMetrics, routePoints.length]);

  const totalDistanceMeters = useMemo(() => {
    if (Number.isFinite(tour?.totalDistanceMeters)) return tour.totalDistanceMeters;
    if (legDistanceMeters > 0) return legDistanceMeters;
    if (polylineDistanceMeters != null && polylineDistanceMeters > 0) return polylineDistanceMeters;
    return null;
  }, [tour, legDistanceMeters, polylineDistanceMeters]);

  const totalDurationSeconds = useMemo(() => {
    const fromTour = toSeconds(tour?.totalDuration);
    if (Number.isFinite(fromTour)) return fromTour;
    if (legDurationSeconds > 0) return legDurationSeconds;
    if (!tourStops.length) return null;
    const nodeDuration = tourStops.reduce((sum, node) => {
      const secs = Number(node?.leg?.durationSeconds);
      return Number.isFinite(secs) ? sum + secs : sum;
    }, 0);
    return nodeDuration || null;
  }, [legDurationSeconds, tourStops]);

  const checkpointCount = useMemo(() => {
    if (!tourStops.length) return 0;
    const typed = tourStops.filter((n) => n?.type === 'checkpoint');
    if (typed.length) return typed.length;
    if (tourStops.length <= 2) return 0;
    return Math.max(tourStops.length - 2, 0);
  }, [tourStops]);

  const origin = tourStops.find((n) => n?.type === 'origin') || tourStops[0] || null;
  const destination =
    tourStops.find((n) => n?.type === 'destination') || tourStops[tourStops.length - 1] || null;

  const telemetryData = useMemo(
    () =>
      buildTelemetryData({
        schedule,
        tour,
        tourStops,
        checkpointCount,
        latestPersonnelLocation: showLiveTelemetry ? latestPersonnelLocation : null,
        recentPersonnelLocations: showLiveTelemetry ? recentPersonnelLocations : [],
        routePoints,
        routeMetrics,
        totalDurationSeconds,
        origin,
        destination,
      }),
    [
      checkpointCount,
      destination,
      latestPersonnelLocation,
      recentPersonnelLocations,
      routeMetrics,
      routePoints,
      origin,
      showLiveTelemetry,
      schedule,
      totalDurationSeconds,
      tour,
      tourStops,
    ],
  );

  const googleTravelMode = useMemo(
    () => resolveTourTravelMode(tour, tourStops).google,
    [tour, tourStops],
  );
  const googleEta = useGoogleEta({
    isActive: showLiveTelemetry,
    scheduleType: schedule?.entity?.type,
    originPosition: telemetryData.position,
    nextCheckpointCenter: telemetryData.nextCheckpointCenter,
    destinationCenter: telemetryData.destinationCenter,
    googleTravelMode,
  });

  const containerStyle = scrollHeight
    ? { height: '100%', maxHeight: scrollHeight, overflowY: 'auto' }
    : {};

  const filteredHistoryScheduleIds = useMemo(() => {
    if (!Array.isArray(historyScheduleIds)) return [];
    return Array.from(
      new Set(
        historyScheduleIds
          .filter((value) => value != null && value !== '')
          .map((value) => `${value}`),
      ),
    );
  }, [historyScheduleIds]);
  const hasCustomHistoryScope = filteredHistoryScheduleIds.length > 0;

  const tabs = useMemo(() => {
    const base = [
      { key: 'live', label: 'Live' },
      { key: 'overview', label: 'Overview' },
    ];
    if (!isFuture && showHistoryTab) base.push({ key: 'history', label: 'Activity History' });
    return base;
  }, [isFuture, showHistoryTab]);

  const [activeTab, setActiveTab] = useState(() => {
    const requested = normalizeTelemetryTab(initialTab, { allowHistory: showHistoryTab });
    return requested || 'live';
  });

  useEffect(() => {
    if (!schedule) {
      setActiveTab('live');
      return;
    }
    if (!tabs.find((tab) => tab.key === activeTab)) {
      setActiveTab(tabs[0]?.key || 'live');
    }
  }, [activeTab, schedule, tabs]);

  if (!schedule) {
    return (
      <Card className="shadow-sm h-100" style={containerStyle}>
        <Card.Body className="d-flex flex-column gap-2">
          <div className="text-muted text-uppercase small">Schedule telemetry</div>
          <div className="fw-semibold fs-5">No schedule selected</div>
          <div className="text-muted">Select a scheduled tour to view telemetry details.</div>
        </Card.Body>
      </Card>
    );
  }

  const badgeLabel = isActive ? 'Active' : isFuture ? 'Upcoming' : 'Idle';
  const isEtaLoading = googleEta.status === 'loading';
  const etaNextDisplay =
    googleEta.next != null ? formatDuration(googleEta.next) : telemetryData.etaToNextCheckpoint;
  const etaDestinationDisplay =
    googleEta.destination != null
      ? formatDuration(googleEta.destination)
      : telemetryData.etaToDestination;

  return (
    <Card className="shadow-sm h-100" style={containerStyle}>
      <Card.Body className="d-flex flex-column gap-3">
        <div className="d-flex justify-content-between align-items-start">
          <div>
            <div className="text-muted text-uppercase small">Schedule telemetry</div>
            <div className="fw-semibold fs-5">
              {schedule.name || `Schedule #${schedule.id ?? 'New'}`}
            </div>
            <div className="small text-muted">{scheduleTargetLabel}</div>
          </div>
          <Badge bg={isActive ? 'primary' : isFuture ? 'warning' : 'secondary'}>{badgeLabel}</Badge>
        </div>

        <div className="d-flex gap-2 flex-wrap">
          {tabs.map((tab) => (
            <TabButton
              key={tab.key}
              active={activeTab === tab.key}
              onClick={() => setActiveTab(tab.key)}
            >
              {tab.label}
            </TabButton>
          ))}
        </div>

        {activeTab === 'live' && (
          <>
            {!showLiveTelemetry ? (
              <div className="text-muted">
                Telemetry data is only available for active tour schedules.
              </div>
            ) : (
              <>
                <Stack gap={3}>
                  {/* Progress Bar */}
                  <div style={{ display: 'flex', alignItems: 'center', gap: 12, width: '100%' }}>
                    <span className="text-muted text-uppercase small" style={{ textAlign: 'left' }}>
                      {telemetryData.checkpointsPassed} / {telemetryData.checkpointsTotal}
                    </span>
                    <div style={{ flex: 1, display: 'flex', gap: 2 }}>
                      {Array.from({ length: telemetryData.checkpointsTotal }).map((_, idx) => (
                        <span
                          key={idx}
                          style={{
                            flex: 1,
                            minWidth: 0,
                            height: 18,
                            borderRadius: 3,
                            background:
                              idx < telemetryData.checkpointsPassed ? '#436c9a' : '#222a2d',
                            // border: '1px solid #adb5bd',
                            marginLeft: idx === 0 ? 0 : 2,
                            marginRight: idx === telemetryData.checkpointsTotal - 1 ? 0 : 0,
                          }}
                        />
                      ))}
                    </div>
                    <span className="small text-muted" style={{ textAlign: 'right' }}>
                      {telemetryData.checkpointsTotal > 0
                        ? `${Math.round((telemetryData.checkpointsPassed / telemetryData.checkpointsTotal) * 100)}%`
                        : '0%'}
                    </span>
                  </div>
                  {/* Combined Status/Checkpoint and ETA as StatTile */}
                  <Row className="g-2">
                    <Col xs={12} md={6}>
                      <StatTile
                        icon={FaClock}
                        label="Status"
                        value={
                          telemetryData.status === 'Traveling' && telemetryData.nextCheckpoint
                            ? `Traveling to ${telemetryData.nextCheckpoint}`
                            : ['In Zone', 'In Checkpoint'].includes(telemetryData.status) &&
                                telemetryData.lastCheckpoint
                              ? `At ${telemetryData.lastCheckpoint}`
                              : telemetryData.status || 'Unknown'
                        }
                      />
                    </Col>
                    {/* ETA to Next Checkpoint as StatTile */}
                    {'etaToNextCheckpoint' in telemetryData && (
                      <Col xs={12} md={6}>
                        <StatTile
                          icon={FaClock}
                          label="ETA to next checkpoint"
                          value={
                            <span className="d-inline-flex align-items-center gap-2">
                              {etaNextDisplay}
                              {isEtaLoading && (
                                <Spinner animation="border" size="sm" className="text-primary" />
                              )}
                            </span>
                          }
                        />
                      </Col>
                    )}
                  </Row>
                  {/* Other Telemetry Fields */}
                  <Row className="g-2">
                    {'travelMode' in telemetryData && (
                      <Col xs={12} md={6}>
                        <StatTile icon={FaRoute} label="Mode" value={telemetryData.travelMode} />
                      </Col>
                    )}
                    {'etaToDestination' in telemetryData && (
                      <Col xs={12} md={6}>
                        <StatTile
                          icon={FaClock}
                          label="ETA to destination"
                          value={
                            <span className="d-inline-flex align-items-center gap-2">
                              {etaDestinationDisplay}
                              {isEtaLoading && (
                                <Spinner animation="border" size="sm" className="text-primary" />
                              )}
                            </span>
                          }
                        />
                      </Col>
                    )}
                  </Row>
                  {/* Render any additional fields from telemetryData */}
                  {/* <div className="d-flex flex-wrap gap-3 mt-2">
                    {Object.entries(telemetryData)
                      .filter(
                        ([key]) =>
                          ![
                            'status',
                            'nextCheckpoint',
                            'lastCheckpoint',
                            'etaToNextCheckpoint',
                            'travelMode',
                            'etaToDestination',
                            'checkpointsPassed',
                            'checkpointsTotal',
                          ].includes(key),
                      )
                      .map(([key, value]) => (
                        <div key={key} style={{ minWidth: 160 }}>
                          <span
                            className="text-muted small"
                            style={{ textTransform: 'capitalize' }}
                          >
                            {key.replace(/([A-Z])/g, ' $1')}
                          </span>
                          <div className="fw-semibold">{String(value)}</div>
                        </div>
                      ))}
                  </div> */}
                  {/* Start and Destination */}
                  <Row className="g-2">
                    <Col xs={12} md={6}>
                      <StatTile
                        icon={() => <FaMapMarkerAlt className="text-success" />}
                        label="Start"
                        value={origin ? pickStopLabel(origin, 'Start not set') : 'Start not set'}
                      />
                    </Col>
                    <Col xs={12} md={6}>
                      <StatTile
                        icon={() => <FaMapMarkerAlt className="text-danger" />}
                        label="Destination"
                        value={
                          destination
                            ? pickStopLabel(destination, 'Destination not set')
                            : 'Destination not set'
                        }
                      />
                    </Col>
                  </Row>
                </Stack>
              </>
            )}
          </>
        )}

        {activeTab === 'overview' && (
          <>
            <Row className="g-2">
              <Col xs={12} md={6}>
                <StatTile
                  icon={FaCalendarAlt}
                  label="Schedule Name"
                  value={schedule.name ?? 'New'}
                />
              </Col>
              <Col xs={12} md={6}>
                <StatTile
                  icon={FaRoute}
                  label="Distance"
                  value={formatDistance(totalDistanceMeters)}
                />
              </Col>
              <Col xs={12} md={6}>
                <StatTile
                  icon={FaClock}
                  label="Estimated time"
                  value={formatDuration(totalDurationSeconds)}
                />
              </Col>
              <Col xs={12} md={6}>
                <StatTile
                  icon={FaUsers}
                  label="Personnel"
                  value={assignedPersonnel.length > 0 ? assignedPersonnel.join(', ') : 'None'}
                />
              </Col>
            </Row>

            <Stack gap={2}>
              <div>
                <strong>Type:</strong> {schedule.entity.type || 'Unknown'}
              </div>
              <div>
                <strong>Target:</strong> {scheduleTargetLabel}
              </div>
              <div>
                <strong>Checkpoints:</strong> {checkpointCount || '0'}
              </div>
              <div>
                <strong>Start:</strong> {formatDateTime(schedule.start_time)}
                <br />
                <strong>End:</strong> {formatDateTime(schedule.end_time)}
              </div>
              {schedule.days_of_week && schedule.days_of_week.length > 0 && (
                <div>
                  <strong>Scheduled days:</strong> {schedule.days_of_week.join(', ')}
                </div>
              )}
              {schedule.notes && (
                <div>
                  <strong>Notes:</strong> {schedule.notes}
                </div>
              )}
            </Stack>
          </>
        )}

        {activeTab === 'history' && (
          <Stack gap={2} className="flex-grow-1" style={{ minHeight: 0 }}>
            <div className="text-muted text-uppercase small">Activity history</div>
            <RunHistory
              scheduleId={hasCustomHistoryScope ? null : schedule.id}
              scheduleIds={hasCustomHistoryScope ? filteredHistoryScheduleIds : null}
              enableRunSelection={enableRunSelection}
              showScheduleColumn={hasCustomHistoryScope}
            />
          </Stack>
        )}
      </Card.Body>
    </Card>
  );
}



